import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { gr } from "../tokens";

const STACK = "@media (max-width: 1099.95px)";

// Page hero (dashboard): teal gradient card, text on the left, stat tiles (GrHeroStat) on the right.
// Text inside is white; admin HTML passed as children inherits the white colour.
export function GrHero({ children, stats, sx }) {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "center",
        gap: gr.space[6],
        padding: `${gr.space[6]} ${gr.space[8]}`,
        borderRadius: gr.radius.card,
        color: gr.color.onBrand,
        background: `linear-gradient(115deg, ${gr.color.brandDark} -40%, ${gr.color.brand} 45%, ${gr.color.brandLight} 100%)`,
        boxShadow: gr.shadow.card,
        fontFamily: gr.font.family,
        "& h1, & h2, & h3, & p, & span, & div": { color: "inherit" },
        [STACK]: { gridTemplateColumns: "1fr", padding: gr.space[6] },
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0 }}>{children}</Box>
      {stats ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(160px, 1fr))",
            gap: gr.space[3],
            "@media (max-width: 599.95px)": {
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            },
          }}
        >
          {stats}
        </Box>
      ) : null}
    </Box>
  );
}

GrHero.propTypes = {
  children: PropTypes.node,
  stats: PropTypes.node,
  sx: PropTypes.object,
};

// Glass stat tile for the hero: label, big number, optional link/hint.
export function GrHeroStat({ label, value, hint }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        padding: `${gr.space[3]} ${gr.space[4]}`,
        borderRadius: gr.radius.tile,
        background: gr.color.onBrandGlass,
        border: `1px solid ${gr.color.onBrandGlassLine}`,
        color: gr.color.onBrand,
      }}
    >
      <Box component="span" sx={{ fontSize: gr.font.size.xs, opacity: 0.9 }}>
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: gr.font.size.h1,
          fontWeight: gr.font.weight.semibold,
          lineHeight: 1.15,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Box>
      {hint ? (
        <Box
          sx={{
            fontSize: gr.font.size.xs,
            // Links stay white on the teal hero; hover / focus underline them. !important only to beat
            // the legacy global `a:hover { color: var(--portal-theme-primary) !important }`, which
            // turned the link teal on the teal background.
            "& a, & button": {
              color: "inherit",
              "&:hover, &:focus-visible": {
                color: "inherit !important",
                textDecoration: "underline",
              },
            },
          }}
        >
          {hint}
        </Box>
      ) : null}
    </Box>
  );
}

GrHeroStat.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.node,
};
